import { BookingSteps, IsInsured, IsDiagnosed } from '@app/store/booking/types'
import { segmentEventsNames } from '@app/utils/analytics'
import { AbTestVariants } from '@app/store/ui'
import { screensData } from '@ui/pages/Booking/screensData'
import { DIAGNOSIS_SUPPORTED_STATES, ROUTES } from '@app/constants'
import { breakPoints } from '@ui/theme'

import { BookingFlowDefinition } from '../types'
import { UnifiedBookingFlowLaunchDarklyFlags } from './types'

export function originalFlow(ldFlags: UnifiedBookingFlowLaunchDarklyFlags): BookingFlowDefinition {
  return {
    flowId: 'original-flow',
    testVariantId: AbTestVariants.Control,
    stepsNumber: 6, // should be max(stepNumber) and not steps.length
    initFlow({ context }) {
      if (context.zipCode) {
        return BookingSteps.ServiceSelection
      }
      return BookingSteps.ZipCode
    },
    includeDiagnosisAsNonQualifyingReason: false,
    steps: [
      // STEPS

      // Step: ZipCode
      {
        stepId: BookingSteps.ZipCode,
        stepNumber: 1,

        heading: screensData.zipCode.heading,
        description: screensData.zipCode.description,
        initBookingflow: true,
        segmentEventNames: {
          bookingStarted: segmentEventsNames.BOOKING_FLOW_STARTED,
          postalCodeCompleted: segmentEventsNames.BOOKING_FLOW_POSTAL_CODE_COMPLETED
        },

        actions: [
          // don't ask for zip code again
          {
            actionId: 'initStep',
            command: ({ backStep, context: { zipCode } }) =>
              !backStep && zipCode && { action: { actionId: 'nextStep' } }
          },
          { actionId: 'nextStep', command: { stepTo: BookingSteps.ServiceSelection } }
        ]
      },

      // Step: Service Selection
      {
        stepId: BookingSteps.ServiceSelection,
        stepNumber: 2,
        contentMaxWidth: 139.8,

        heading: screensData.serviceSeletion.heading,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_SERVICE_SELECTION_COMPLETED,

        actions: [
          { actionId: 'nextStep', command: { stepTo: BookingSteps.Age } },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.ZipCode } }
        ]
      },

      // Step: Age
      {
        stepId: BookingSteps.Age,
        stepNumber: 3,

        heading: screensData.age.heading,
        description: screensData.age.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_AGE_COMPLETED,

        actions: [
          { actionId: 'nextStep', command: { stepTo: BookingSteps.Diagnosis } },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.ServiceSelection } }
        ]
      },

      // Step: Diagnosis
      {
        stepId: BookingSteps.Diagnosis,
        stepNumber: 4,

        heading: screensData.diagnosis.heading,
        description: screensData.diagnosis.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_DIAGNOSIS_COMPLETED,

        actions: [
          {
            actionId: 'nextStep',
            command: ({ isDiagnosed, provinceCode }) => {
              let nextStepId = BookingSteps.Insurance

              if (ldFlags && ldFlags.diagnosisOfframp && [IsDiagnosed.No, IsDiagnosed.Unknown].includes(isDiagnosed)) {
                if (provinceCode && DIAGNOSIS_SUPPORTED_STATES.includes(provinceCode)) {
                  nextStepId = BookingSteps.DiagnosisOfframp
                }
              }
              return {
                stepTo: nextStepId
              }
            }
          },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.Age } }
        ]
      },

      // Step-1: Insurance
      {
        stepId: BookingSteps.Insurance,
        stepNumber: 5,

        heading: screensData.insurance.heading,
        description: screensData.insurance.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_INSURANCE_COMPLETED,

        actions: [
          {
            actionId: 'nextStep',
            command: ({ isInsured, isUserQualified }) => ({
              stepTo:
                isInsured === IsInsured.Yes
                  ? BookingSteps.Provider
                  : isUserQualified
                  ? BookingSteps.ParentInfo
                  : BookingSteps.NotQualified
            })
          },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.Diagnosis } }
        ]
      },

      // Step-2: Provider
      {
        stepId: BookingSteps.Provider,
        stepNumber: 5,

        heading: screensData.provider.heading,
        description: screensData.provider.description,
        segmentEventName: segmentEventsNames.BOOKING_FLOW_INSURANCE_COMPLETED,

        actions: [
          {
            actionId: 'nextStep',
            command: ({ isUserQualified }) => ({
              stepTo: isUserQualified ? BookingSteps.ParentInfo : BookingSteps.NotQualified
            })
          },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.Insurance } }
        ]
      },

      // Step: ParentInfo
      {
        stepId: BookingSteps.ParentInfo,
        stepNumber: 6,

        heading: screensData.parentInfo.heading,
        description: screensData.parentInfo.description,
        showEmail: true,

        actions: [
          { actionId: 'nextStep', command: { stepTo: BookingSteps.TimeSlots } },
          {
            actionId: 'previousStep',
            // isInsured is not coming from this step, but from another step, therefore it can be taken from 'context'
            command: ({
              context: {
                bookingState: {
                  inputData: { isInsured }
                }
              }
            }) => ({
              stepBackTo: isInsured === IsInsured.Yes ? BookingSteps.Provider : BookingSteps.Insurance
            })
          }
        ]
      },

      // SCHEDULING

      // TimeSlots
      {
        stepId: BookingSteps.TimeSlots,
        heading: screensData.timeSlots.heading,

        description: ({ isWorkingTime }) =>
          isWorkingTime
            ? screensData.timeSlots.description.workingHours
            : screensData.timeSlots.description.nonWorkingHours,

        segmentEventNames: {
          callNow: segmentEventsNames.BOOKING_FLOW_CALL_REQUESTED
        },
        callNowTitle: 'Call Me Now',
        callLaterTitle: 'Schedule a Call',
        continueTitle: 'Continue',
        popUpTitle: 'Choose a Date and Time for a Call',
        popUpSubTitle: 'Please choose a date and time for a 30 minute call below.',
        facebookPixelEventsBookingCallMeNowClickedOnlyWithSessionId: false,
        googleAnalyticsEventsCallRequested: true,

        actions: [
          { actionId: 'callLater', command: { stepTo: BookingSteps.SchedulingConfirmation } },
          { actionId: 'callNow', command: { stepTo: BookingSteps.CallNowConfirmation } }
        ]
      },

      // SchedulingConfirmation
      {
        stepId: BookingSteps.SchedulingConfirmation,
        heading: screensData.schedulingConfirmation.heading,

        description: ({ windowWidth }) =>
          windowWidth <= breakPoints.laptopL
            ? screensData.schedulingConfirmation.description.mobile
            : screensData.schedulingConfirmation.description.desktop,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_CALL_SCHEDULED,
        confirmationButtonTall: false,
        showPhoneNumber: false,
        confirmButtonTitle: 'Confirm',
        googleAnalyticsEventsCallScheduled: true,

        actions: [
          { actionId: 'nextStep', command: { stepTo: BookingSteps.CallLaterConfirmation } },
          { actionId: 'previousStep', command: { stepBackTo: BookingSteps.TimeSlots } }
        ]
      },

      // FINAL SCREENS

      // CallNowConfirmation
      {
        stepId: BookingSteps.CallNowConfirmation,

        heading: screensData.callConfirmation.callNow.heading,
        description: screensData.callConfirmation.callNow.description,
        imageSrc: screensData.callConfirmation.callNow.imageSrc,
        imageAlt: screensData.callConfirmation.callNow.imageAlt,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_ADDED_TO_CALENDAR,
        documentsForCallDataVersion: 'control',

        actions: []
      },

      // CallLaterConfirmation
      {
        stepId: BookingSteps.CallLaterConfirmation,
        heading: screensData.callConfirmation.callLater.heading,
        description: screensData.callConfirmation.callLater.description,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_ADDED_TO_CALENDAR,
        showPhoneNumber: false,
        documentsForCallDataVersion: 'control',
        goToHomePageLinkTitle: 'Go to Homepage',

        actions: []
      },

      // DiagnosisOfframp
      {
        stepId: BookingSteps.DiagnosisOfframp,

        heading: screensData.diagnosisOfframp.heading,
        description: screensData.diagnosisOfframp.description,
        imageSrc: screensData.diagnosisOfframp.imageSrc,
        imageAlt: screensData.diagnosisOfframp.imageAlt,

        segmentEventName: segmentEventsNames.BOOKING_FLOW_DIAGNOSIS_OFFRAMP,

        actions: [{ actionId: 'previousStep', command: { stepBackTo: BookingSteps.Diagnosis } }]
      },

      // NotQualified
      {
        stepId: BookingSteps.NotQualified,

        heading: screensData.notQualified.heading,
        imageSrc: screensData.notQualified.imageSrc,
        imageAlt: screensData.notQualified.imageAlt,
        segmentEventNames: {
          view: segmentEventsNames.BOOKING_FLOW_NOT_QUALIFIED_VIEW,
          submit: segmentEventsNames.BOOKING_FLOW_NOT_QUALIFIED_CONTACT_SUBMIT
        },
        reasonListFormat: 'control',
        showEmail: true,

        actions: [
          {
            actionId: 'homePage',
            command: { navigateTo: ROUTES.homePage }
          }
          // no previous step intentionally
        ]
      }
    ]
  }
}
